import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CalendarIcon, Users, Mail, Phone, User, LogIn } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { MapEmbed } from '../components/MapEmbed';
import { api } from '../api';
import { Price } from '../context/CurrencyContext';

const Booking = ({ user, authChecked }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { destination: stateDestination, package: pkg } = location.state || {};

  const [destinations, setDestinations] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(stateDestination || null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', travelers: 1, specialRequests: '' });
  const [travelDate, setTravelDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!stateDestination && !pkg) {
      api.get('/destinations').then((res) => setDestinations(res.data)).catch(() => {});
    }
  }, [stateDestination, pkg]);

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({ ...prev, name: prev.name || user.name || '', email: prev.email || user.email || '' }));
    }
  }, [user]);

  const item = pkg || selectedDestination;
  const price = item?.price || 0;
  const travelers = Number(formData.travelers) || 1;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!item) {
      toast.error('Please select a destination');
      return;
    }
    if (!travelDate) {
      toast.error('Please select a travel date');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/bookings', {
        destination: pkg ? pkg.destinations.join(', ') : selectedDestination.name,
        package_name: pkg?.name || null,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        travel_date: format(travelDate, 'yyyy-MM-dd'),
        travelers,
        special_requests: formData.specialRequests || null,
      });
      toast.success('Booking inquiry submitted successfully! We will contact you soon.');
      navigate('/my-bookings');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to submit a booking');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to submit booking. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <p data-testid="booking-loading" className="text-gray-600 text-lg">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16 px-4">
        <Card className="border-0 shadow-xl max-w-md w-full" data-testid="booking-login-required">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <LogIn className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Login to Book</h2>
            <p className="text-gray-600 mb-6">
              Please sign in with Google to submit a booking inquiry{item ? ` for ${item.name}` : ''}.
            </p>
            <Button
              data-testid="booking-login-btn"
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
            >
              Login Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16" data-testid="booking-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8 text-center">Book Your Trip</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl">Booking Details</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
                  {!stateDestination && !pkg && (
                    <div>
                      <Label className="mb-2 block">Destination *</Label>
                      <Select
                        value={selectedDestination ? String(selectedDestination.id) : ''}
                        onValueChange={(val) => setSelectedDestination(destinations.find((d) => String(d.id) === val))}
                      >
                        <SelectTrigger data-testid="booking-destination-select" className="border-2">
                          <SelectValue placeholder="Choose a destination" />
                        </SelectTrigger>
                        <SelectContent>
                          {destinations.map((d) => (
                            <SelectItem key={d.id} value={String(d.id)} data-testid={`booking-destination-option-${d.name.toLowerCase()}`}>
                              {d.name} — {d.location}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="name" className="flex items-center mb-2"><User className="w-4 h-4 mr-2" />Full Name *</Label>
                    <Input data-testid="booking-name-input" id="name" name="name" value={formData.name} onChange={handleInputChange} required placeholder="Enter your full name" className="border-2" />
                  </div>

                  <div>
                    <Label htmlFor="email" className="flex items-center mb-2"><Mail className="w-4 h-4 mr-2" />Email Address *</Label>
                    <Input data-testid="booking-email-input" id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required placeholder="your.email@example.com" className="border-2" />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="flex items-center mb-2"><Phone className="w-4 h-4 mr-2" />Phone Number *</Label>
                    <Input data-testid="booking-phone-input" id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required placeholder="+91 98765 43210" className="border-2" />
                  </div>

                  <div>
                    <Label className="flex items-center mb-2"><CalendarIcon className="w-4 h-4 mr-2" />Preferred Travel Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button data-testid="booking-date-trigger" type="button" variant="outline" className="w-full justify-start text-left font-normal border-2">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {travelDate ? format(travelDate, 'PPP') : 'Select a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={travelDate} onSelect={setTravelDate} disabled={(date) => date < new Date()} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label htmlFor="travelers" className="flex items-center mb-2"><Users className="w-4 h-4 mr-2" />Number of Travelers *</Label>
                    <Input data-testid="booking-travelers-input" id="travelers" name="travelers" type="number" min="1" max="20" value={formData.travelers} onChange={handleInputChange} required className="border-2" />
                  </div>

                  <div>
                    <Label htmlFor="specialRequests" className="mb-2 block">Special Requests (Optional)</Label>
                    <Textarea data-testid="booking-requests-input" id="specialRequests" name="specialRequests" value={formData.specialRequests} onChange={handleInputChange} placeholder="Any special requirements or requests..." rows={4} className="border-2" />
                  </div>

                  <Button
                    data-testid="booking-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white py-6 text-lg"
                  >
                    {submitting ? 'Submitting...' : 'Submit Booking Inquiry'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="border-0 shadow-xl sticky top-24" data-testid="booking-summary">
              <CardHeader className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-t-lg">
                <CardTitle>Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {item ? (
                  <>
                    <img src={item.image} alt={item.name} className="w-full h-40 object-cover rounded-lg mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-testid="booking-summary-name">{item.name}</h3>
                    {item.location && <p className="text-gray-600 text-sm mb-1">{item.location}</p>}
                    <p className="text-gray-600 text-sm mb-4">{item.duration}</p>
                    {item.map_query && (
                      <MapEmbed query={item.map_query} className="w-full h-40 rounded-lg mb-4" testId="booking-summary-map" />
                    )}
                    <div className="border-t pt-4">
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-600">Price per person:</span>
                        <Price inr={price} className="font-semibold" />
                      </div>
                      <div className="flex justify-between text-lg font-bold text-teal-600">
                        <span>Total:</span>
                        <Price inr={price * travelers} testId="booking-summary-total" />
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-500 text-sm">Select a destination to see your summary.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;
